import { canonicalEntry, type CanonicalEntry } from "./canonical-entry.js";

/**
 * The 词典 entries this device has already been told, kept between sessions.
 *
 * **A worker's memory, or a page's, is the wrong lifetime for this.** A
 * background worker is torn down after about thirty seconds of inactivity and
 * a page lasts as long as the tab, and `canonical.json` is served
 * `max-age=300`, so a reader who looks something up twice ten minutes apart
 * pays for every word of it twice. Measured over the 31 words of a news
 * paragraph, that is 224 ms and 31 round trips, for answers this device
 * already had. The dictionary tiers have had a store of their own since the
 * conversion tools were built and the entries behind them never did.
 *
 * The segmenter's word list is the heavier of the two readers. A pasted
 * article is a few hundred distinct words rather than a selection's thirty,
 * and most of a second paste is words the first one already asked about.
 *
 * **A day, which is what the site already considers fresh enough.** The edge
 * serves these objects under `s-maxage=86400`, so a reader is looking at
 * something up to a day old whether or not this store exists. An entry older
 * than that is fetched again and the store is written; an entry younger is
 * answered from here and nothing goes over the network at all.
 *
 * **A stored answer outlives a failure.** A request that will not complete
 * falls back to whatever is here, however old, because a meaning from
 * yesterday beats no meaning at all. `Glosses` is what decides that.
 *
 * **Nothing here is required.** The Cache API needs a secure context and can
 * be absent, blocked or full. Every method answers as though the store were
 * empty rather than failing, the same posture `dictionary-cache.ts` takes and
 * for the same reason: a reader without one gets the panel and the word list
 * exactly as they behaved before this existed.
 *
 * Nothing is evicted by hand. An entry is about 300 bytes, so a reader who has
 * looked up ten thousand distinct words is holding a few megabytes, and the
 * browser evicts the whole store under storage pressure. A policy of our own
 * would cost more code than the disk it saved.
 */

/** The store's name, namespaced because the site shares an origin. */
const storeName = "chineseboost.glosses";

/** How long an answer is served without asking the site again. */
export const glossFreshnessMs = 86_400_000;

/** An answer the site gave, and when it gave it. */
export interface StoredGloss {
  /** When this device was told, as milliseconds since the epoch. */
  readonly at: number;

  /** What it was told, or nothing where the site has no entry for the word. */
  readonly entry: CanonicalEntry | undefined;
}

/** The store, where the browser has one to give. */
async function glossCache(): Promise<Cache | undefined> {
  try {
    return typeof caches === "undefined"
      ? undefined
      : await caches.open(storeName);
  } catch {
    return undefined;
  }
}

/** What a store needs told, both of which have an answer of their own. */
export interface GlossStoreProperties {
  /** The clock freshness is measured against. */
  readonly now?: () => number;

  /** Where the store comes from, so a test needs no browser. */
  readonly open?: () => Promise<Cache | undefined>;
}

/** What this device has been told about words, from earlier sittings. */
export class GlossStore {
  readonly #now: () => number;
  readonly #open: () => Promise<Cache | undefined>;
  #opening: Promise<Cache | undefined> | undefined;

  constructor(properties: GlossStoreProperties = {}) {
    this.#now = properties.now ?? Date.now;
    this.#open = properties.open ?? glossCache;
  }

  /** The store, opened at most once per session. */
  #store(): Promise<Cache | undefined> {
    this.#opening ??= this.#open();

    return this.#opening;
  }

  /** Whether a stored answer is young enough to be given without asking. */
  fresh(stored: StoredGloss): boolean {
    return this.#now() - stored.at < glossFreshnessMs;
  }

  /** What is stored for a word, however old, or nothing. */
  async held(url: string): Promise<StoredGloss | undefined> {
    try {
      const store = await this.#store();
      const response = await store?.match(url);

      return response === undefined
        ? undefined
        : storedGloss(await response.json());
    } catch {
      return undefined;
    }
  }

  /**
   * Write down what the site said, or quietly do not.
   *
   * Storage can be full and a browser can refuse it outright. Failing here
   * would fail a look-up that has everything it needs, so it does not. What is
   * lost is the free answer next time.
   */
  async keep(url: string, entry: CanonicalEntry | undefined): Promise<void> {
    try {
      const store = await this.#store();
      const stored = { at: this.#now(), entry: asPublished(entry) };

      await store?.put(url, Response.json(stored));
    } catch {
      // As above. The panel has its meaning; only the next look-up is poorer.
    }
  }
}

/**
 * An entry written back in the shape the site published it in.
 *
 * **The store holds what the site said rather than what was made of it.** The
 * two differ in one field, the band arriving as `hsk` and an ungraded entry
 * spelling it as an empty string, and writing our own shape would mean a
 * second reader for it beside `canonicalEntry`. One encode here is cheaper
 * than two validators that have to agree.
 */
function asPublished(entry: CanonicalEntry | undefined): unknown {
  return entry === undefined
    ? null
    : {
        word: entry.word,
        pinyin: entry.pinyin,
        hsk: entry.band ?? "",
        meanings: entry.meanings,
      };
}

/**
 * What a stored object says, or nothing where it says nothing usable.
 *
 * Read defensively because the store outlives the code that wrote it. A
 * browser can hold an entry written by a version of this code that shaped
 * it differently, and an unreadable one has to mean "ask the site" rather than
 * "the site has no entry".
 */
function storedGloss(body: unknown): StoredGloss | undefined {
  if (typeof body !== "object" || body === null) {
    return undefined;
  }

  const fields = body as Record<string, unknown>;
  const at = fields["at"];

  return typeof at === "number"
    ? { at, entry: canonicalEntry(fields["entry"]) }
    : undefined;
}
